import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Link2, CheckCircle2, XCircle, Loader2, ChevronRight,
  Sparkles, RefreshCw, Play, ShieldCheck, Mail, X,
  FileBarChart2, Repeat as RepeatIcon,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useCompany } from "@/lib/company";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Cadence of the migration-status poll while a job is running. QBO
// migrations for a mid-size realm typically finish in 30-90s; the
// 2s poll gives smooth progress feedback without hammering the API.
const POLL_MS = 2000;

const ENTITY_LABELS = {
  Account: "Chart of Accounts",
  Customer: "Customers",
  Vendor: "Vendors",
  Item: "Items",
  Invoice: "Invoices",
  Bill: "Bills",
  Payment: "Payments (received)",
  BillPayment: "Bill Payments",
  JournalEntry: "Journal Entries",
  Deposit: "Deposits",
  Transfer: "Transfers",
  CreditMemo: "Credit Memos",
  SalesReceipt: "Sales Receipts",
  RefundReceipt: "Refund Receipts",
  Purchase: "Purchases / Expenses",
  Attachable: "Attachments",
};

export default function QboConnect() {
  const { currentId } = useCompany();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [drill, setDrill] = useState(null);
  // Two-step dialog flow for kicking off a migration:
  //   1. `confirmOpen` — pre-flight modal that promises an email on
  //      completion. Replaces the browser-native `confirm()` so the
  //      copy actually reads like a product.
  //   2. `startedOpen` — post-start modal that reassures the user
  //      they can safely close the tab; the email is on its way.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [startedOpen, setStartedOpen] = useState(false);
  const pollRef = useRef(null);

  const refreshStatus = async () => {
    if (!currentId) return;
    const r = await api.get(`/companies/${currentId}/qbo/status`);
    setStatus(r.data);
    // Rehydrate the "Migration complete" summary + preview card on
    // page revisit. Backend caches:
    //   * `preview` — the last preview_counts click (survives refresh)
    //   * `last_job` — the most recent terminal migration with stats
    // Only populate LOCAL state if we haven't already got a job in-
    // flight (avoids clobbering a live poll with a stale cached row).
    if (r.data.preview && !preview) setPreview(r.data.preview);
    if (r.data.last_job && !job) setJob(r.data.last_job);
  };
  useEffect(() => { refreshStatus(); }, [currentId]);

  // OAuth callback lands with ?qbo=connected — toast + refresh state.
  // On failure it lands with ?qbo_error=<reason>, which we surface as
  // an error toast (with the raw reason string so we can diagnose).
  useEffect(() => {
    if (params.get("qbo") === "connected") {
      toast.success("QuickBooks Online connected");
      refreshStatus();
      params.delete("qbo"); params.delete("realm");
      setParams(params, { replace: true });
    }
    const qErr = params.get("qbo_error");
    if (qErr) {
      toast.error(`QBO connection failed: ${qErr}`, { duration: 20000 });
      params.delete("qbo_error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/qbo/oauth/start`);
      window.location.href = r.data.url;
    } catch (e) {
      // Cred-config failures come back as 500 with a detail like
      // "QBO PRODUCTION credentials not configured — missing env
      // var(s): QBO_CLIENT_ID_PROD". Surface for 20s so the admin
      // can screenshot / act on it.
      const detail = e.response?.data?.detail || "Failed to start QBO connection";
      const isConfigError = /not configured/i.test(detail);
      toast.error(detail, {
        duration: isConfigError ? 20000 : 6000,
      });
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect from QuickBooks Online?\n\nThis revokes access at Intuit. Imported data stays in your ledger.")) return;
    setBusy(true);
    try {
      await api.post(`/companies/${currentId}/qbo/disconnect`);
      toast.success("Disconnected");
      setStatus({ connected: false });
      setPreview(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/companies/${currentId}/qbo/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const startMigration = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const r = await api.post(`/companies/${currentId}/qbo/migrations`);
      setJob({ job_id: r.data.job_id, status: "queued", processed: 0 });
      startPolling(r.data.job_id);
      // Follow-up "we've got it" modal — safe-to-close reassurance.
      setStartedOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Migration failed to start");
    } finally { setBusy(false); }
  };

  const pollOnce = async (jid) => {
    try {
      const r = await api.get(`/companies/${currentId}/qbo/migrations/${jid}`);
      setJob(r.data);
      if (r.data.status === "done" || r.data.status === "failed" || r.data.status === "cancelled") {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (r.data.status === "done") toast.success("Migration complete");
        else if (r.data.status === "failed") toast.error(`Migration failed: ${r.data.error || "unknown"}`);
      }
    } catch (e) { /* transient — keep polling */ }
  };
  const startPolling = (jid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollOnce(jid);
    pollRef.current = setInterval(() => pollOnce(jid), POLL_MS);
  };
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const previewTotal = preview?.total ?? 0;
  const running = job && (job.status === "queued" || job.status === "running");
  const done = job && job.status === "done";

  // Wizard progression — determine which step is the "active" one
  // so we can highlight ONE button in filled emerald + downgrade
  // earlier completed steps to the outline (border) variant. The user's
  // eye always lands on the next thing to do.
  //   activeStep = 1 (connect) → 2 (preview) → 3 (migrate) → 4 (mirror)
  //
  // Prior versions had a "Verify migration" step at 4 that let users
  // upload a QBO Balance Sheet PDF for AI-driven side-by-side diff.
  // Removed Feb 28 2026 — the Recon Panel on the P&L / BS report
  // pages now fetches QBO's own reports live via API and shows
  // per-account drift inline, making the PDF-upload step redundant.
  let activeStep = 1;
  if (status?.connected) activeStep = 2;
  if (preview)           activeStep = 3;
  if (done)              activeStep = 4;
  const filled  = "px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2";
  const outline = "px-4 py-2 rounded-md border-2 border-emerald-600 bg-white text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center gap-2";
  const btnCls  = (step) => activeStep === step ? filled : outline;

  return (
    <div className="p-6 max-w-5xl" data-testid="qbo-connect-page">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600 grid place-items-center text-white shrink-0">
          <Link2 size={26} />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">QuickBooks Online</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Connect your QuickBooks Online company and migrate every account, contact, item,
            transaction and attachment into SmartBooks. One-way import — your QBO data stays
            untouched. Safe to re-run: matched by QBO ID.
          </p>
        </div>
      </div>

      {/* Step 1: Connect */}
      <section className="rounded-xl border bg-white p-5 mb-4" data-testid="qbo-step-connect">
        <div className="flex items-center gap-3 mb-3">
          <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold ${
            status?.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}>1</span>
          <h2 className="font-heading font-semibold text-slate-900">Connect</h2>
          {status?.connected && (
            <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-700 font-medium">
              <CheckCircle2 size={14} /> Connected
              {status.environment && (
                <span className="ml-1 text-slate-500">· {status.environment}</span>
              )}
            </span>
          )}
        </div>
        {status?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="text-slate-600">
                <div>Realm ID: <span className="font-mono text-xs">{status.realm_id}</span></div>
                <div className="text-xs text-slate-400 mt-1">Connected {status.connected_at?.slice(0, 10)}</div>
              </div>
              <button
                onClick={disconnect}
                disabled={busy}
                data-testid="qbo-disconnect-btn"
                className="px-3 py-1.5 text-xs rounded-md border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-40"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={connect}
            disabled={busy || !currentId}
            data-testid="qbo-connect-btn"
            className="px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Connect to QuickBooks Online
          </button>
        )}
      </section>

      {/* Step 2: Preview */}
      <section className={`rounded-xl border bg-white p-5 mb-4 transition-opacity ${
        status?.connected ? "" : "opacity-40 pointer-events-none"
      }`} data-testid="qbo-step-preview">
        <div className="flex items-center gap-3 mb-3">
          <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold ${
            preview ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}>2</span>
          <h2 className="font-heading font-semibold text-slate-900">Preview scope</h2>
          {preview && (
            <span className="ml-auto text-xs text-slate-500">
              {previewTotal.toLocaleString()} records across {Object.keys(preview.counts).length} object types
            </span>
          )}
        </div>
        {!preview ? (
          <button
            onClick={runPreview}
            disabled={busy || !status?.connected}
            data-testid="qbo-preview-btn"
            className={btnCls(2)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Preview what will import
          </button>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {Object.entries(preview.counts).map(([e, n]) => {
              const has = n > 0;
              return (
                <button
                  key={e}
                  disabled={!has || !done}
                  onClick={() => has && done && setDrill({
                    entity_type: e,
                    label: ENTITY_LABELS[e] || e,
                  })}
                  className={`flex items-center justify-between border rounded-md
                              px-3 py-2 text-left transition ${
                    has && done
                      ? "hover:border-indigo-400 hover:shadow-sm cursor-pointer"
                      : "cursor-default opacity-90"
                  }`}
                  data-testid={`qbo-preview-tile-${e}`}
                >
                  <span className="text-slate-700">{ENTITY_LABELS[e] || e}</span>
                  <span className="flex items-center gap-1.5">
                    <span className={`font-mono text-xs ${n < 0 ? "text-red-500" : "text-slate-900"}`}>
                      {n < 0 ? "—" : n.toLocaleString()}
                    </span>
                    {has && done && <ChevronRight size={12} className="text-slate-400" />}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Raw drill-down modal for the Preview tiles. Only opens after
          migration is complete — before then, the entities aren't in
          our DB yet. Aug 22 2026. */}
      <QboEntityDrill
        cid={currentId}
        drill={drill}
        onClose={() => setDrill(null)}
      />

      {/* Step 3: Migrate */}
      <section className={`rounded-xl border bg-white p-5 mb-4 transition-opacity ${
        preview ? "" : "opacity-40 pointer-events-none"
      }`} data-testid="qbo-step-migrate">
        <div className="flex items-center gap-3 mb-3">
          <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold ${
            done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
          }`}>3</span>
          <h2 className="font-heading font-semibold text-slate-900">Migrate</h2>
          {job && (
            <span className="ml-auto text-xs">
              {running && <span className="inline-flex items-center gap-1 text-blue-700"><Loader2 size={12} className="animate-spin" /> {job.status}</span>}
              {done && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={12} /> Complete</span>}
              {job.status === "failed" && <span className="inline-flex items-center gap-1 text-red-700"><XCircle size={12} /> Failed</span>}
            </span>
          )}
        </div>
        {!job ? (
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={busy || !preview}
            data-testid="qbo-migrate-btn"
            className={btnCls(3)}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Start migration
          </button>
        ) : (
          <div className="text-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-700">
                {job.entity ? `Importing ${ENTITY_LABELS[job.entity] || job.entity}…` : job.phase}
              </span>
              <span className="font-mono text-xs text-slate-500">
                {(job.processed || 0).toLocaleString()} records
              </span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all"
                style={{
                  width: `${done ? 100 : Math.min(90, ((job.processed || 0) / Math.max(1, previewTotal)) * 100)}%`,
                }}
              />
            </div>
            {job.error && (
              <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
                <b>Error:</b> {job.error}
              </div>
            )}
            {done && (
              <div
                className="mt-3 rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-cyan-50 px-4 py-3"
                data-testid="qbo-migration-complete-banner"
              >
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-emerald-900">
                      Migration complete
                    </div>
                    <div className="text-xs text-emerald-800/80 mt-0.5">
                      Your QuickBooks Online data is now mirrored. Here's what we did after the import:
                    </div>
                    <dl className="mt-2 grid grid-cols-2 sm:grid-cols-6 gap-2 text-xs">
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-seeded-deactivated"
                      >
                        <dt className="text-emerald-700/70">Seeded accounts tidied</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          {(job.seeded_deactivated ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-estimates-pulled"
                      >
                        <dt className="text-emerald-700/70">Estimates pulled</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          {(job.mirror_estimates_pulled ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-pos-pulled"
                      >
                        <dt className="text-emerald-700/70">Purchase orders pulled</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          {(job.mirror_pos_pulled ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-opening-inventory"
                        title="Total dollar value posted to 1300 Inventory Asset for on-hand inventory as of migration"
                      >
                        <dt className="text-emerald-700/70">Opening inventory</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          ${(job.opening_inventory_value ?? 0)
                            .toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </dd>
                      </div>
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-inv-adjustments"
                        title="Historical inventory adjustments (writeups, writedowns, count corrections) pulled from QBO and posted as journal entries"
                      >
                        <dt className="text-emerald-700/70">Inv adjustments</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          {(job.mirror_inv_adj_pulled ?? 0).toLocaleString()}
                        </dd>
                      </div>
                      <div
                        className="rounded-md bg-white/70 border border-emerald-100 px-2.5 py-1.5"
                        data-testid="qbo-stat-skipped-dupkey"
                        title="Duplicate rows we detected and adopted instead of re-inserting"
                      >
                        <dt className="text-emerald-700/70">Duplicates adopted</dt>
                        <dd className="font-semibold text-emerald-900 tabular-nums">
                          {(job.skipped_dupkey ?? 0).toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}
            {/* Re-run migration — visible after a successful import so
                the user can force a fresh full-scope pull without
                disconnecting first. Migration is idempotent (records
                are keyed by QBO id) so this is safe. Aug 22 2026. */}
            {done && (
              <div className="mt-3 flex items-center justify-between
                               rounded-lg border bg-slate-50 px-4 py-3">
                <div className="text-xs text-slate-600">
                  Need to pull fresh data from QuickBooks? Re-run the
                  full-scope migration. Safe to repeat.
                </div>
                <button
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5
                             rounded-md bg-indigo-600 text-white text-sm
                             hover:bg-indigo-500 disabled:opacity-50"
                  data-testid="qbo-remigrate-btn"
                >
                  <RepeatIcon size={14} /> Re-run migration
                </button>
              </div>
            )}
            {/* NOTE (Feb 2026): The post-migration action button
                row (View CoA / View Contacts / Review Plaid
                Categories / Open Live Mirror / Re-run migration /
                Resend email / Rebuild account hierarchy /
                Categorize imported transactions) was intentionally
                removed per user request. The endpoints still exist
                and are reachable directly from the sidebar /
                settings pages; only the inline shortcuts were
                pulled from this card. Kept a placeholder here to
                make the intent obvious to future readers so nobody
                re-adds them without a design conversation. */}
          </div>
        )}
      </section>

      {/* QBO Reports snapshot — pull the raw BS and P&L from QBO and
          store them in `qbo_report_snapshots`. Exposes both bases and
          lets users refresh without disconnecting. Uses the same
          storage as the Recon Panel drift view. Aug 22 2026. */}
      <QboReportsPanel currentId={currentId}
                        connected={!!status?.connected} />

      {/* Step 4: Open Live Mirror — always visible when connected so
          returning users can jump into the bi-directional sync
          without needing to re-run migration. Bi-directional sync is
          the real long-lived value prop, so it deserves its own step.
          Prior "Verify migration" step (PDF upload → AI-diff) was
          removed Feb 28 2026 — superseded by the live Recon Panel
          inside the P&L / Balance Sheet report views. */}
      <section className={`rounded-xl border bg-white p-5 mb-4 transition-opacity ${
        status?.connected ? "" : "opacity-40 pointer-events-none"
      }`} data-testid="qbo-step-mirror">
        <div className="flex items-center gap-3 mb-3">
          <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-800 inline-flex items-center justify-center text-sm font-medium">
            4
          </span>
          <h2 className="font-heading font-semibold text-slate-900">Open Live Mirror</h2>
          <span className="text-[10px] uppercase tracking-wide bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">Live</span>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Bi-directional sync. Any change you make here — new invoices, bills,
          purchases, deposits, edits, deletes — flows to QBO within seconds.
          Any change in QBO comes back on the next pull. The dry-run preview
          shows exactly what would move before anything ships.
        </p>
        <button
          onClick={() => navigate("/settings/qbo-mirror")}
          data-testid="qbo-mirror-open-btn"
          className={outline}
        >
          <RefreshCw size={14} />
          Open Live Mirror
        </button>
      </section>

      <div className="text-xs text-slate-400 mt-6 flex items-start gap-2">
        <Sparkles size={12} className="mt-0.5 text-slate-300" />
        <span>
          Full-scope import covers every entity (Chart of Accounts, Customers,
          Vendors, Items, Invoices, Bills, Payments, Journal Entries,
          Estimates, Purchase Orders, Purchases, Sales Receipts, Deposits).
          Once connected the Live Mirror keeps everything in sync bi-directionally —
          you won't need to reconnect.
        </span>
      </div>

      {/* Confirm-start dialog — replaces the native `confirm()` so the
          copy can promise the completion email. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="qbo-migrate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Play size={16} className="text-emerald-600" />
              Start QuickBooks migration?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  We&apos;ll import every listed record from QuickBooks Online
                  into your ledger. Safe to re-run — records are matched by
                  QBO ID, so duplicates won&apos;t stack up.
                </p>
                <div className="flex items-start gap-2 rounded-md bg-cyan-50 border border-cyan-200 p-3 text-cyan-900">
                  <Mail size={14} className="mt-0.5 shrink-0" />
                  <span className="text-xs leading-relaxed">
                    Migrations can take a few minutes. You can safely close
                    this tab — we&apos;ll <b>email you as soon as it&apos;s done</b>.
                  </span>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="qbo-migrate-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="qbo-migrate-confirm-start"
              onClick={startMigration}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Start migration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post-start dialog — reassures the user the email is coming so
          they don't sit and watch the progress bar. */}
      <Dialog open={startedOpen} onOpenChange={setStartedOpen}>
        <DialogContent data-testid="qbo-migrate-started-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail size={16} className="text-cyan-600" />
              We&apos;re migrating your QuickBooks data
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600 pt-1">
                <p>
                  Your import is running in the background — nothing to
                  babysit. As soon as it wraps up, we&apos;ll send you an
                  email with the final counts and a link back to your books.
                </p>
                <p className="text-xs text-slate-500">
                  You can close this tab or keep it open to watch progress —
                  either way works.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              data-testid="qbo-migrate-started-ack"
              onClick={() => setStartedOpen(false)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-cyan-600 text-white text-sm hover:bg-cyan-700"
            >
              Got it
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---------- QBO Reports panel (raw BS + P&L snapshots) ------------

function QboReportsPanel({ currentId, connected }) {
  const [tab, setTab] = useState("BalanceSheet");
  const [basis, setBasis] = useState("Accrual");
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!currentId || !connected) return;
    setLoading(true);
    api.get(`/companies/${currentId}/qbo/reports/latest`, {
      params: { report_name: tab, accounting_method: basis },
    }).then(r => setSnapshot(r.data?.snapshot || null))
      .catch(() => setSnapshot(null))
      .finally(() => setLoading(false));
  }, [currentId, connected, tab, basis]);

  const refresh = async () => {
    setRunning(true);
    try {
      // Snapshot both bases in one click so switching the toggle is instant.
      await Promise.all([
        api.post(`/companies/${currentId}/qbo/reports/snapshot`, null,
                  { params: { accounting_method: "Accrual" } }),
        api.post(`/companies/${currentId}/qbo/reports/snapshot`, null,
                  { params: { accounting_method: "Cash" } }),
      ]);
      toast.success("Refreshed QBO reports");
      // Re-fetch the currently-visible one.
      const r = await api.get(`/companies/${currentId}/qbo/reports/latest`, {
        params: { report_name: tab, accounting_method: basis },
      });
      setSnapshot(r.data?.snapshot || null);
    } catch (e) {
      toast.error("Failed to refresh reports");
    } finally {
      setRunning(false);
    }
  };

  const rows = useMemo(() => {
    const p = snapshot?.payload;
    if (!p) return [];
    const out = [];
    const walk = (rs, depth) => {
      (rs || []).forEach((r) => {
        const cd  = r.ColData || (r.Summary && r.Summary.ColData) || [];
        const hcd = (r.Header && r.Header.ColData) || [];
        const scd = (r.Summary && r.Summary.ColData) || [];
        const inner = (r.Rows && r.Rows.Row) || [];
        if (cd.length && !inner.length && !r.Summary) {
          out.push({ kind: "data", label: cd[0]?.value || "",
                     values: cd.slice(1).map(c => c.value || ""), depth });
          return;
        }
        if (inner.length) {
          if (hcd.length) {
            out.push({ kind: "header",
                       label: hcd[0]?.value || "",
                       values: hcd.slice(1).map(c => c.value || ""), depth });
          }
          walk(inner, depth + 1);
          if (scd.length) {
            out.push({ kind: "total", label: scd[0]?.value || "",
                       values: scd.slice(1).map(c => c.value || ""), depth });
          }
          return;
        }
        if (scd.length) {
          out.push({ kind: "total", label: scd[0]?.value || "",
                     values: scd.slice(1).map(c => c.value || ""), depth });
        }
      });
    };
    walk((p.Rows && p.Rows.Row) || [], 0);
    return out;
  }, [snapshot]);

  const fmt = (v) => {
    if (v === "" || v == null) return "";
    const n = Number(v);
    if (Number.isNaN(n)) return v;
    const s = Math.abs(n).toLocaleString(undefined,
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? `(${s})` : s;
  };

  return (
    <section className={`rounded-xl border bg-white p-5 mb-4 transition-opacity ${
      connected ? "" : "opacity-40 pointer-events-none"
    }`} data-testid="qbo-reports-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="rounded-lg bg-amber-100 p-2">
          <FileBarChart2 className="w-4 h-4 text-amber-600" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold text-slate-900">QBO Reports</div>
          <div className="text-xs text-slate-500">
            Raw Balance Sheet & Profit and Loss from QuickBooks. Cash
            and Accrual, unchanged. Refresh anytime without
            disconnecting.
          </div>
        </div>
        <button onClick={refresh} disabled={running || !connected}
                 data-testid="qbo-reports-refresh-btn"
                 className="inline-flex items-center gap-1.5 px-3 py-2
                            rounded-md bg-amber-600 text-white text-sm
                            hover:bg-amber-500 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin"/>
                    : <RefreshCw className="w-4 h-4"/>}
          {running ? "Pulling…" : snapshot ? "Refresh reports" : "Pull reports"}
        </button>
      </div>

      {/* Selector */}
      <div className="flex flex-wrap gap-2 border-t pt-3">
        {["BalanceSheet", "ProfitAndLoss"].map((n) => (
          <button key={n} onClick={() => setTab(n)}
                   className={`px-3 py-1.5 rounded-lg text-sm border ${
                     tab === n
                       ? "bg-indigo-600 border-indigo-600 text-white"
                       : "hover:bg-slate-50"
                   }`}
                   data-testid={`qbo-report-tab-${n}`}>
            {n === "BalanceSheet" ? "Balance Sheet" : "Profit & Loss"}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs">
          {["Accrual", "Cash"].map((b) => (
            <button key={b} onClick={() => setBasis(b)}
                     className={`px-2.5 py-1 rounded-md ${
                       basis === b
                         ? "bg-slate-900 text-white"
                         : "text-slate-500 hover:text-slate-700"
                     }`}
                     data-testid={`qbo-report-basis-${b}`}>
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-auto rounded-lg border max-h-[520px]">
        {loading ? (
          <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin"/> Loading report…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            No snapshot yet. Click <b>Pull reports</b> to fetch from QuickBooks.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-slate-500
                                 uppercase text-[11px] tracking-wide">Account</th>
                <th className="text-right px-4 py-2 font-medium text-slate-500
                                 uppercase text-[11px] tracking-wide">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={
                  r.kind === "header" ? "font-semibold text-slate-800"
                  : r.kind === "total" ? "font-medium bg-slate-50/70 border-t"
                  : "text-slate-700"}>
                  <td className="px-4 py-1.5"
                       style={{ paddingLeft: 16 + r.depth * 18 }}>
                    {r.label}
                  </td>
                  <td className="px-4 py-1.5 text-right font-mono tabular-nums">
                    {fmt(r.values[r.values.length - 1])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {snapshot?.snapshot_at && (
        <div className="text-[11px] text-slate-400 mt-2">
          Fetched · {new Date(snapshot.snapshot_at).toLocaleString()}
        </div>
      )}
    </section>
  );
}



// ---------- Production entity raw drill-down ---------------------
// Same modal shape as Test QBO's EntityDrillDown but hits the
// production endpoint that reads from `db.invoices` / `db.bills`
// / `db.transactions` / etc. (not `qbo_test_raw`).

function QboEntityDrill({ cid, drill, onClose }) {
  const [rows, setRows]   = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!drill || !cid) return;
    setLoading(true);
    api.get(`/companies/${cid}/qbo/entity/${drill.entity_type}`,
             { params: { limit: 100 } })
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => toast.error("Failed to load entity"))
      .finally(() => setLoading(false));
  }, [drill, cid]);

  return (
    <Dialog open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{drill?.label}</span>
            <span className="text-xs font-mono px-2 py-0.5 rounded
                              bg-slate-100 text-slate-500">
              {drill?.entity_type}
            </span>
            <span className="text-xs text-slate-500 font-normal">
              · showing {rows?.length ?? 0} of {total}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto rounded-lg border bg-slate-50">
          {loading && (
            <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading raw rows…
            </div>
          )}
          {!loading && rows && rows.length === 0 && (
            <div className="p-6 text-sm text-slate-500">No rows.</div>
          )}
          {!loading && rows && rows.map((r, i) => (
            <details key={i} className="border-b last:border-none">
              <summary className="cursor-pointer p-3 text-sm font-mono
                                   hover:bg-white">
                <span className="text-slate-500">{drill?.entity_type} · </span>
                <span>Id {r.qbo_id}</span>
                {r.display && (
                  <span className="ml-2 text-slate-700 font-sans">
                    {r.display}
                  </span>
                )}
                {r.amount !== undefined && r.amount !== null && (
                  <span className="ml-2 text-slate-500 font-sans">
                    · ${r.amount}
                  </span>
                )}
                {r.date && (
                  <span className="ml-2 text-slate-500 font-sans">
                    · {r.date}
                  </span>
                )}
              </summary>
              <pre className="text-[11px] p-3 pt-0 whitespace-pre-wrap
                              break-all text-slate-600">
                {JSON.stringify(r.raw, null, 2)}
              </pre>
            </details>
          ))}
        </div>
        <DialogFooter>
          <button onClick={onClose}
                   className="inline-flex items-center gap-1.5 px-3 py-1.5
                              rounded-lg text-sm border hover:bg-slate-50">
            <X className="w-4 h-4" /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

